/**
 * Papéis (roles) reconhecidos pela aplicação.
 *
 * ÚNICA fonte da verdade das strings de papel — se a API usar outro token,
 * ajuste aqui e em nenhum outro lugar.
 *
 * `COLABORADOR` — papel padrão; acesso às funcionalidades do próprio colaborador.
 * `RH`         — acesso ao módulo de Recursos Humanos.
 * `DIRETORIA`  — acesso ao Dashboard de RH (somente leitura da visão gerencial).
 * `TI`         — login de testes da equipe de TI; tem todos os acessos (ver `possuiPapel`).
 * `FINANCEIRO` — módulo Financeiro (consulta e escrita). A DIRETORIA só consulta.
 * `SUPERVISOR_*` — consulta do Inventário. Os regionais só enxergam itens de
 *                  colaboradores da própria região (escopo aplicado pela API, que
 *                  mapeia papel→região na config `Inventario`); o geral vê tudo.
 * `PLANEJADOR` — Operacional: roteiros, unidades, equipes e calendário de visitas.
 * `ANALISTA`   — Operacional: revisão dos relatórios de visita (string da API: `ANALISTA_RELATORIO`).
 *
 * As strings reais emitidas pela API em `usuario.papeis` ainda não foram
 * confirmadas pelo back-end — se divergirem, ajuste só aqui.
 */
export const ROLES = {
  COLABORADOR: 'COLABORADOR',
  RH: 'RH',
  DIRETORIA: 'DIRETORIA',
  TI: 'TI',
  FINANCEIRO: 'FINANCEIRO',
  SUPERVISOR_BRASILIA: 'SUPERVISOR_BRASILIA',
  SUPERVISOR_GOIAS: 'SUPERVISOR_GOIAS',
  SUPERVISOR_GERAL: 'SUPERVISOR_GERAL',
  PLANEJADOR: 'PLANEJADOR',
  ANALISTA: 'ANALISTA_RELATORIO',
} as const

export type Role = (typeof ROLES)[keyof typeof ROLES]

/** Gestão do Inventário: cadastros, entrega/transferência/devolução/baixa e termos. */
export const INVENTARIO_GESTAO: readonly Role[] = [ROLES.DIRETORIA, ROLES.RH]

/** Consulta do Inventário (listas, detalhe, resumos e ações operacionais). */
export const INVENTARIO_CONSULTA: readonly Role[] = [
  ...INVENTARIO_GESTAO,
  ROLES.SUPERVISOR_BRASILIA,
  ROLES.SUPERVISOR_GOIAS,
  ROLES.SUPERVISOR_GERAL,
]

/**
 * "Minha equipe" (itens agrupados por responsável): supervisores e Diretoria.
 * O RH acompanha pelos filtros da lista de itens.
 */
export const INVENTARIO_MINHA_EQUIPE: readonly Role[] = [
  ROLES.DIRETORIA,
  ROLES.SUPERVISOR_BRASILIA,
  ROLES.SUPERVISOR_GOIAS,
  ROLES.SUPERVISOR_GERAL,
]

/** Supervisor restrito à própria região (vê só itens com responsável da região). */
export const SUPERVISORES_REGIONAIS: readonly Role[] = [
  ROLES.SUPERVISOR_BRASILIA,
  ROLES.SUPERVISOR_GOIAS,
]

/** Dashboard de RH completo (KPIs, custos, movimentação) e páginas de consulta do módulo. */
export const RH_DASHBOARD: readonly Role[] = [ROLES.DIRETORIA, ROLES.RH]

/** Bloco de atestados da Visão Geral RH — o supervisor geral só enxerga este. */
export const RH_DASHBOARD_ATESTADOS: readonly Role[] = [...RH_DASHBOARD, ROLES.SUPERVISOR_GERAL]

/** Consulta do Financeiro (GETs: listas, detalhes, histórico, anexos e dashboard). */
export const FINANCEIRO_CONSULTA: readonly Role[] = [ROLES.FINANCEIRO, ROLES.DIRETORIA]

/** Escrita do Financeiro (POST/PUT/DELETE). A DIRETORIA fica de fora: só consulta. */
export const FINANCEIRO_ESCRITA: readonly Role[] = [ROLES.FINANCEIRO]

/**
 * Operacional — roteiros, unidades, equipes e escrita do calendário (agendar/efetivar sugestão,
 * remarcar, cancelar). A consulta do calendário é `OPERACIONAL_CALENDARIO`.
 */
export const OPERACIONAL_PLANEJAMENTO: readonly Role[] = [ROLES.PLANEJADOR]

/**
 * Operacional — consulta do calendário (visitas, sugestões, detalhe e histórico). As ações de
 * escrita são liberadas por papel da ação (`OPERACIONAL_PLANEJAMENTO`/`OPERACIONAL_REABERTURA`).
 */
export const OPERACIONAL_CALENDARIO: readonly Role[] = [
  ROLES.PLANEJADOR,
  ROLES.ANALISTA,
  ROLES.DIRETORIA,
]

/**
 * Edição da unidade (dados cadastrais, região/localização e roteiros), feita no mesmo
 * modal pelo contrato do Financeiro e pela tela de unidades do Operacional.
 */
export const UNIDADE_ESCRITA: readonly Role[] = [ROLES.FINANCEIRO, ROLES.PLANEJADOR]

/** Operacional — revisão dos relatórios (aprovar/devolver). */
export const OPERACIONAL_REVISAO: readonly Role[] = [ROLES.ANALISTA]

/**
 * Operacional — reabertura de visita APROVADA: volta para REALIZADA (fila de revisão)
 * e descarta o PDF, que é gerado de novo na próxima aprovação.
 */
export const OPERACIONAL_REABERTURA: readonly Role[] = [ROLES.PLANEJADOR, ROLES.ANALISTA]

/** Operacional — histórico de relatórios aprovados (download dos PDFs). */
export const OPERACIONAL_RELATORIOS: readonly Role[] = [
  ROLES.DIRETORIA,
  ROLES.PLANEJADOR,
  ROLES.ANALISTA,
]

/** Operacional — painel e detalhe da visita (somente leitura). A DIRETORIA só vê isto. */
export const OPERACIONAL_PAINEL: readonly Role[] = [
  ROLES.PLANEJADOR,
  ROLES.ANALISTA,
  ROLES.DIRETORIA,
]

/**
 * Único ponto de decisão de acesso por papel: `true` se o usuário tiver algum
 * dos `roles` exigidos. O papel TI passa em qualquer checagem — não repita
 * `|| papel === 'TI'` em outros lugares.
 */
export function possuiPapel(
  userRoles: readonly string[] | undefined,
  roles: readonly string[],
): boolean {
  if (!userRoles?.length) return false
  if (userRoles.includes(ROLES.TI)) return true
  return roles.some((role) => userRoles.includes(role))
}
